<!--
 * @Description: 未开票-服务商/供应商，都可以发起自己对应类型的发票
 * @Date: 2025-06-11 16:21:13
-->

<template>
  <XrkTable
    ref="xrkTableRef"
    :getListAjax="getListAjax"
    :searchConfig="searchConfig"
    :tableConfig="tableConfig"
  ></XrkTable>
</template>
<script lang="tsx" setup>
import { ref, getCurrentInstance } from 'vue';
import {
  XrkTable,
  XrkTableProps,
  useTableColumn,
  useSelectColumn,
  useInputColumn,
  BaseButton,
  useCreateModal
} from 'xrk-components';
import { formatter } from '@/utils';
import { PAY_PASSAGEWAY_MAPPING } from '@/common/constants/bank';
import { notInvoicedList } from '@V/invoice/src/data-source/apis';
import { ORDER_TYPE } from '@constants/order';
import InvoiceOpenDialog from './invoice-open-dialog';
import { InvoiceWtdk1Dialog } from './invoice-wtdk';
import { useRouteMeta } from '@hooks/common';
import { useJumpToFinishedOrderList, useJumpToOrderDetail } from '@hooks/order';
import { IS_PLATFORM_TYPE_1, PLATFORM_IS_FEIGON } from '@/config';

type OrderTypeOption = { label: string; value: number };

const filterFeigonOrderType = <T extends OrderTypeOption>(orderTypes: T[]) => {
  if (!PLATFORM_IS_FEIGON) return orderTypes;
  return orderTypes.filter(item => ![4, 5].includes(item.value));
};

const FUNCTION_CODE_ORDER_TYPE = {
  1: filterFeigonOrderType([
    ORDER_TYPE[1],
    ORDER_TYPE[4],
    ORDER_TYPE[5],
    ORDER_TYPE[6],
    ORDER_TYPE[8]
  ]),
  2: filterFeigonOrderType([
    ORDER_TYPE[1],
    ORDER_TYPE[4],
    ORDER_TYPE[5],
    ORDER_TYPE[8]
  ]),
  3: [ORDER_TYPE[6]],
  8: [ORDER_TYPE[1], ORDER_TYPE[10]]
};

const xrkTableRef = ref();
const routeMeta = useRouteMeta();
const isSupplier = [3].includes(+routeMeta.functionCode);
const isAsp = [2].includes(+routeMeta.functionCode);
const isDbgys = routeMeta.functionCode == 8;
const currentOrderType = FUNCTION_CODE_ORDER_TYPE[routeMeta.functionCode];

const getListAjax = async params => {
  return notInvoicedList(params);
};

const searchConfig = ref<XrkTableProps['searchConfig']>({
  searchColumns: [
    useInputColumn({
      prop: 'theSellerName',
      label: '企业名称',
      config: {
        type: 'text',
        placeholder: '请输入'
      }
    }),
    useInputColumn({
      prop: 'userDefinedOrderNo',
      label: '订单编号',
      config: {
        type: 'text',
        placeholder: '请输入'
      }
    }),
    useSelectColumn({
      prop: 'userDefinedOrderType',
      label: '订单类型',
      defaultValue: currentOrderType[0].value,
      config: {
        clearable: false,
        placeholder: '请选择',
        options: currentOrderType
      }
    }),
    useSelectColumn({
      prop: 'payPassagewayCode',
      label: '支付通道',
      config: {
        placeholder: '请选择',
        options: PAY_PASSAGEWAY_MAPPING
      }
    }),
    useInputColumn({
      prop: 'projectName',
      label: '项目名称',
      config: {
        type: 'text',
        placeholder: '请输入'
      }
    })
  ],
  searchBtn: {
    show: true,
    text: '搜索'
  },
  resetBtn: {
    show: true,
    text: '重置'
  },
  otherComponents: [
    (isAsp || isSupplier) && {
      render() {
        return (
          <>
            <BaseButton
              v-resource={[['未开票订单_申请开票']]}
              type="primary2"
              click={async () => {
                useCreateModal(
                  InvoiceOpenDialog,
                  {
                    onSuccess() {
                      xrkTableRef.value.initCurrentList();
                    }
                  },
                  getCurrentInstance()
                );
              }}
            >
              申请开票
            </BaseButton>
          </>
        );
      }
    },
    isAsp &&
      IS_PLATFORM_TYPE_1 && {
        render() {
          return (
            <>
              <BaseButton
                v-resource={[['未开票订单_申请委托代开发票']]}
                type="primary2"
                click={async () => {
                  useCreateModal(InvoiceWtdk1Dialog, {
                    onSuccess() {
                      xrkTableRef.value.initCurrentList();
                    }
                  });
                }}
              >
                申请委托代开发票
              </BaseButton>
            </>
          );
        }
      }
  ].filter(item => item)
});

const tableConfig = ref<XrkTableProps['tableConfig']>({
  tableColumns: [
    useTableColumn({
      type: 'index',
      label: '序号',
      width: '80px'
    }),
    useTableColumn({
      label: '购买方',
      prop: 'purchaserName',
      width: 300
    }),
    useTableColumn({
      label: '项目名称',
      prop: 'projectName',
      width: 300
    }),
    useTableColumn({
      label: '订单编号',
      prop: 'userDefinedOrderNo',
      width: '260px',
      render({ row }) {
        return isDbgys ? (
          <span>{row.userDefinedOrderNo}</span>
        ) : (
          <BaseButton
            link
            onClick={() => {
              useJumpToFinishedOrderList(row.userDefinedOrderNo, {
                pageName: 'finished',
                method: 'push'
              });
            }}
          >
            {row.userDefinedOrderNo}
          </BaseButton>
        );
      }
    }),
    useTableColumn({
      label: '订单类型',
      prop: 'userDefinedOrderType',
      formatter: ({ cellValue }) =>
        formatter.defaultFormat(cellValue, () => ORDER_TYPE[cellValue].label)
    }),
    useTableColumn({
      label: '销售方',
      prop: 'theSellerName',
      width: 300
    }),
    useTableColumn({
      label: '收款时间',
      prop: 'receiveAt',
      width: '180px',
      formatter: ({ cellValue }) => formatter.date(cellValue)
    }),
    useTableColumn({
      label: '支付通道',
      prop: 'payPassagewayCode',
      formatter: ({ cellValue }) =>
        formatter.defaultFormat(
          cellValue,
          () => PAY_PASSAGEWAY_MAPPING[cellValue].label
        )
    }),
    useTableColumn({
      label: '金额',
      prop: 'userDefinedPrice',
      width: '180px',
      render({ row }) {
        return isDbgys ? (
          <span> {formatter.fen2yuan(row.userDefinedPrice)}</span>
        ) : (
          <BaseButton
            link
            onClick={() => {
              useJumpToOrderDetail(row.userDefinedOrderNo, { method: 'push' });
            }}
          >
            {formatter.fen2yuan(row.userDefinedPrice)}
          </BaseButton>
        );
      }
    })
  ]
});
</script>
